import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { of, timer } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MsalService } from '../msal';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	refreshSubscription: any;
	constructor(public router: Router, private localStorageService: LocalStorageService, private msalService: MsalService) {}

	/**
	 * Handles the login be calling authorize on auth0
	 * @param signInType number 0 or 1 to represent emcloud AD login vs Site Minder login
	 */
	public login(signInType): void {
		this.msalService.loginRedirect({
			scopes: environment.msal.apiScopes
		});
	}

	/**
	 * Handles authenticating the user if they aren't already, otherwise it goes to retrieve
	 * the user object and scheduling the token renewal timer
	 */
	public async handleAuthentication(account = null): Promise<void> {
		if (!account) {
			const allAccounts = this.msalService.getAllAccounts();
			if (allAccounts.length > 1) {
				const userAccount = this.msalService.getAccountByUsername(this.localStorageService.getUserEmail());
				
				if (userAccount) {
					account = userAccount;
				} else {
					this.clearLocalStorage();
					this.router.navigate(['']);
					return;
				}
			} else if (allAccounts.length === 1) {
				account = allAccounts[0];
			} else {
				return;
			}
		}
		if (!this.isAuthenticated() && account) {
			this.msalService.acquireTokenSilent({ scopes: environment.msal.apiScopes, account: account }).subscribe(
				token => {
					if (token && token.accessToken && token.idToken) {
						this.setSession(token);
						this.loadUser();
					}
				},
				// error => {
				// 	// this.msalService.acquireTokenPopup({ scopes: environment.msal.apiScopes }).subscribe(
				// 	// 	popupToken => {
				// 	// 		if (popupToken && popupToken.accessToken && popupToken.idToken) {
				// 	// 			this.setSession(popupToken);
				// 	// 			this.loadUser();
				// 	// 		}
				// 	// 	},
				// 		error => {
				// 			console.log(error)
				// 		}
				// 	);
				//}
					error => {
						console.log(error)
						}
			);
		} else if (account) {
			this.loadUser();
			this.scheduleRenewal(account);
		}
	}

	/**
	 * Dispatches the GetMe action and creates an observable.
	 * If there is an error getting the user, the page is directed to the 'forbidden' screen
	 * If we get the user, navigate to default component
	 */
	private loadUser(): void {
		// Load user from api
		this.router.navigate(['']);
	}

	/**
	 * Sets the session data returned from auth0 and then schedules a timer
	 * for renewing the token
	 * @param authResult
	 */
	private setSession(authResult): void {
		// Set the time that the Access Token will expire at
		const expiresAt = authResult.expiresOn.getTime();
		this.localStorageService.setAccessToken(authResult.accessToken);
		this.localStorageService.setIdToken(authResult.idToken);
		this.localStorageService.setExpiresAt(expiresAt);
		this.localStorageService.setUserEmail(authResult.account.username);
		this.scheduleRenewal(authResult.account);
	}

	/**
	 * Clears local storage
	 */
	clearLocalStorage() {
		this.localStorageService.clearStorage();
	}

	/**
	 * Handles the logout by clearing local storage and then logging out of
	 * the federated service
	 */
	public logout(): void {
		this.msalService.logout({ postLogoutRedirectUri: environment.msal.logoutUri });
		this.clearLocalStorage();
	}

	/**
	 * Check whether the current time is past the
	 * Access Token's expiry time
	 */
	public isAuthenticated(): boolean {
		const expiresAt = JSON.parse(this.localStorageService.getExpirestAt() || '{}');
		return new Date().getTime() < expiresAt;
	}

	/**
	 * This function handles renewing an access token. If the user has not been active in the app
	 * for a given period of time the user will be logged out. Otherwise the users sessions will
	 * be checked and a new access token will be retrieved
	 */
	public renewTokens(account): void {
		if (this.localStorageService.getLastActivity() && parseFloat(this.localStorageService.getActivityTimeout()) - Date.now() < 0) {
			this.logout();
		} else {
			if (!account) return;
			this.msalService.acquireTokenSilent({ scopes: environment.msal.apiScopes, account: account }).subscribe(
				token => {
					if (token && token.accessToken && token.idToken) {
						this.setSession(token);
					}
				},
				error => {
					console.error(`Could not get a new token.`);
					this.logout();
				}
			);
		}
	}

	/**
	 * Sets up a timer to renew the token. This timer will trye to renew the token
	 * once it expires. Additionally, it will unsubscribe from any previous timers.
	 */
	public scheduleRenewal(account) {
		if (!this.isAuthenticated()) {
			return;
		}
		this.unscheduleRenewal();

		const expiresAt = this.localStorageService.getExpirestAt() * 1;

		const source = of(expiresAt).pipe(
			mergeMap(expAt => {
				const now = Date.now();

				// Use the delay in a timer to
				// run the refresh at the proper time
				return timer(Math.max(1, expAt - now));
			})
		);

		// Once the delay time from above is
		// reached, get a new JWT and schedule
		// additional refreshes
		this.refreshSubscription = source.subscribe(() => {
			this.renewTokens(account);
			this.scheduleRenewal(account);
		});
	}

	/**
	 * Unsubscribes from previous renewal timer
	 */
	public unscheduleRenewal() {
		if (!this.refreshSubscription) {
			return;
		}
		this.refreshSubscription.unsubscribe();
	}
}
